import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { VqlArtefactParameter } from '../../types/velociraptor'

type ParameterChangeHandler =
  | ((artefactName: string, paramName: string, value: string) => void)
  | undefined

interface ParameterInputProps {
  artefactName: string
  param: VqlArtefactParameter
  currentValue: string
  onParameterChange?: ParameterChangeHandler
}

function ParameterInput({
  artefactName,
  param,
  currentValue,
  onParameterChange,
}: ParameterInputProps): ReactElement {
  const { t } = useTranslation('cases')
  const inputClass =
    'w-full text-xs px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-1 focus:ring-[#4e5e82] focus:border-[#4e5e82]'

  if (param.paramType === 'bool') {
    return (
      <select
        value={currentValue}
        onChange={(e) => onParameterChange?.(artefactName, param.name, e.target.value)}
        className={inputClass}
      >
        <option value="true">{t('common.yes', 'Yes')}</option>
        <option value="false">{t('common.no', 'No')}</option>
      </select>
    )
  }

  if (param.choices && param.choices.length > 0) {
    return (
      <select
        value={currentValue}
        onChange={(e) => onParameterChange?.(artefactName, param.name, e.target.value)}
        className={inputClass}
      >
        {param.choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    )
  }

  return (
    <input
      type={param.paramType === 'int' ? 'number' : 'text'}
      value={currentValue}
      placeholder={param.defaultValue ?? ''}
      onChange={(e) => onParameterChange?.(artefactName, param.name, e.target.value)}
      className={inputClass}
    />
  )
}

interface ParameterRowProps {
  artefactName: string
  param: VqlArtefactParameter
  currentValue: string
  onParameterChange?: ParameterChangeHandler
}

function ParameterRow({
  artefactName,
  param,
  currentValue,
  onParameterChange,
}: ParameterRowProps): ReactElement {
  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
            {param.name}
          </span>
          <span className="text-xs text-slate-400 dark:text-slate-500">({param.paramType})</span>
          {param.required && <span className="text-xs text-[#4e5e82]">*</span>}
        </div>
        {param.description && (
          <p className="mt-0.5 truncate text-xs text-slate-400 dark:text-slate-500">
            {param.description}
          </p>
        )}
      </div>
      <div className="w-48 flex-shrink-0">
        <ParameterInput
          artefactName={artefactName}
          param={param}
          currentValue={currentValue}
          onParameterChange={onParameterChange}
        />
      </div>
    </div>
  )
}

interface ArtefactParameterListProps {
  artefactName: string
  parameters: VqlArtefactParameter[]
  overrides?: Record<string, string | number | boolean> | undefined
  onParameterChange?: ParameterChangeHandler
}

export function ArtefactParameterList({
  artefactName,
  parameters,
  overrides = {},
  onParameterChange,
}: ArtefactParameterListProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="space-y-2 px-10 pb-3">
      <p className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('velociraptor.parametersLabel', 'Parameters')}
      </p>
      <div className="space-y-1.5">
        {parameters.map((param) => {
          const currentValue =
            overrides[param.name] !== undefined
              ? String(overrides[param.name])
              : (param.defaultValue ?? '')
          return (
            <ParameterRow
              key={param.name}
              artefactName={artefactName}
              param={param}
              currentValue={currentValue}
              onParameterChange={onParameterChange}
            />
          )
        })}
      </div>
    </div>
  )
}
